/**
 * 近 24 小时 per-signal trip / release counts for the 保障配置 drawer (R7, G13).
 *
 * Reads the existing guaranteed-center events read (platform
 * `/internal/guaranteed-service/events`, newest first, one bounded page per
 * kind for this SKU). A trip / release is a `target_probe_trip` /
 * `target_probe_recover` / `shadow_trip` / `shadow_recover` row whose
 * `trip_reason` (or `shadow_trip_reason`) is a guard fault kind. A full page
 * whose oldest row is still inside the window may hide older rows of any
 * signal (probe / quality trips crowd the page too), so every signal's count
 * from that page is a lower bound: "≥n", or "unknown" when the page held none
 * of that signal (R1-A7) — never a plain 0. Hit and failover counts have no
 * bounded read yet; the drawer says so instead of guessing.
 */
import {
  GUARANTEED_EVENT_MAX_PAGE_SIZE,
  GUARD_SIGNAL_KINDS,
  guardSignalOfFaultKind,
  type GuardSignalKind,
  type GuaranteedServiceEventRecord,
  type ListGuaranteedServiceEventsResponse,
} from '@nadoutong/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { opsApiRequest } from '../../../api/client.ts';
import { useSession } from '../../../hooks/useSession.tsx';

export const GUARD_STATS_WINDOW_MS = 24 * 60 * 60 * 1000;

const STAT_KINDS = ['target_probe_trip', 'target_probe_recover', 'shadow_trip', 'shadow_recover'] as const;
type StatKind = (typeof STAT_KINDS)[number];

export interface GuardCount {
  readonly n: number;
  /** The page was full and still inside the window: at least `n`. */
  readonly capped: boolean;
}

export interface GuardSignalStats {
  readonly trips: GuardCount;
  readonly shadowTrips: GuardCount;
  readonly releases: GuardCount;
}

export type GuardStats = Readonly<Partial<Record<GuardSignalKind, GuardSignalStats>>>;

function reasonOf(event: GuaranteedServiceEventRecord, kind: StatKind): string | null {
  const details = event.details;
  if (details === null) return null;
  const raw = kind === 'shadow_trip' ? details.shadow_trip_reason : details.trip_reason;
  return typeof raw === 'string' ? raw : null;
}

type StatField = keyof GuardSignalStats;

function fieldOf(kind: StatKind): StatField {
  if (kind === 'target_probe_trip') return 'trips';
  if (kind === 'shadow_trip') return 'shadowTrips';
  return 'releases';
}

/** Pure fold of one page per kind into per-signal counts. */
export function foldGuardStats(
  pages: Readonly<Record<StatKind, readonly GuaranteedServiceEventRecord[]>>,
  nowMs: number,
  pageSize: number,
): GuardStats {
  const since = nowMs - GUARD_STATS_WINDOW_MS;
  const counts = new Map<GuardSignalKind, Record<StatField, number>>();
  const cappedFields = new Set<StatField>();
  for (const kind of STAT_KINDS) {
    const events = pages[kind];
    const oldest = events.at(-1);
    const field = fieldOf(kind);
    if (events.length >= pageSize && oldest !== undefined && Date.parse(oldest.event_ts) >= since) cappedFields.add(field);
    for (const event of events) {
      if (Date.parse(event.event_ts) < since) continue;
      const reason = reasonOf(event, kind);
      const signal = reason === null ? undefined : guardSignalOfFaultKind(reason);
      if (signal === undefined) continue;
      const current = counts.get(signal.kind) ?? { trips: 0, shadowTrips: 0, releases: 0 };
      counts.set(signal.kind, { ...current, [field]: current[field] + 1 });
    }
  }
  const out: Partial<Record<GuardSignalKind, GuardSignalStats>> = {};
  for (const signal of GUARD_SIGNAL_KINDS) {
    const current = counts.get(signal);
    if (current === undefined && cappedFields.size === 0) continue;
    const countOf = (field: StatField): GuardCount => ({
      n: current?.[field] ?? 0,
      capped: cappedFields.has(field),
    });
    out[signal] = { trips: countOf('trips'), shadowTrips: countOf('shadowTrips'), releases: countOf('releases') };
  }
  return out;
}

export function useGuardStats(skuId: string): UseQueryResult<GuardStats> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['guaranteed-guard-config', 'stats', skuId],
    queryFn: async () => {
      const read = async (kind: StatKind): Promise<readonly GuaranteedServiceEventRecord[]> => {
        const search = new URLSearchParams({
          page: '1',
          page_size: String(GUARANTEED_EVENT_MAX_PAGE_SIZE),
          sku_id: skuId,
          kind,
        });
        const body = await opsApiRequest<ListGuaranteedServiceEventsResponse>(
          `/v1/ops/guaranteed-service/events?${search.toString()}`,
          {},
          fetchImpl,
        );
        return body.events;
      };
      const [trips, releases, shadow, shadowReleases] = await Promise.all(STAT_KINDS.map(read));
      return foldGuardStats(
        {
          target_probe_trip: trips ?? [],
          target_probe_recover: releases ?? [],
          shadow_trip: shadow ?? [],
          shadow_recover: shadowReleases ?? [],
        },
        Date.now(),
        GUARANTEED_EVENT_MAX_PAGE_SIZE,
      );
    },
    staleTime: 60_000,
  });
}
